import { Box, Button, Checkbox, FormControlLabel, IconButton, Radio, TextField, Typography, Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTheme } from '@mui/material'
import { cardSx, cardHeaderSx, truncateText } from './constants'
import { GetRequest, PostRequest, PostRequestV2 } from '../../../common/api'
import { urls } from '../../../common/constants'
import useToken from '../../../helpers/useTokens'
import { useSetAtom } from 'jotai'
import { alertAtom, globalLoaderAtom } from '../../../common/jotaiState/globalStateAtoms'
import LineinputV2 from '../../../common/lineinputV2'
import { Item, SingleInputModal, SuccessPopUp } from '../../../common/snippets'

export const SmsPolicy = () => {
  const [IsStandard, setIsStandard] = useState(1)
  const setLoader = useSetAtom(globalLoaderAtom)
  const setAlert = useSetAtom(alertAtom)
  const scrollRef = useRef(null)
  const containerRef = useRef(null)
  const theme = useTheme()
  const { loginData } = useToken()
  const { intCompanyID, intUserID } = loginData
  const [tableData, setTableData] = useState([])
  const [tags, setTags] = useState([])
  const [availableHeight, setAvailableHeight] = useState(null)
  const [SMSSenderID, setSMSSenderID] = useState('')
  const [SMSEntityID, setSMSEntityID] = useState('')
  const [isDLTLock, setIsDLTLock] = useState(0)
  const [templateIds, setTemplateIds] = useState({ standard: 0, customised: 0 })
  const [policyData, setPolicyData] = useState({})
  const [otpModalOpen, setOtpModalOpen] = useState(false)
  const [otpValue, setOtpValue] = useState('')
  const [otpType, setOtpType] = useState('')
  const [otpAction, setOtpAction] = useState('')
  const [testSmsModalOpen, setTestSmsModalOpen] = useState(false)
  const [testSmsPhoneNumber, setTestSmsPhoneNumber] = useState('')
  const [selectedTestSmsCard, setSelectedTestSmsCard] = useState(null)
  const [switchConfirmCard, setSwitchConfirmCard] = useState(null)
  const scrollCards = (direction) => {
    if (!scrollRef.current) return
    scrollRef.current.scrollBy({ left: direction * 520, behavior: 'smooth' })
  }
  const to01 = (value) => (value === 1 || value === '1' || value === true || value === 'true' ? 1 : 0)
  const fetchData = () => {
    GetRequest(urls.getSMSBookingPolicies, { intCompanyID })
      .then((res) => {
        if (res.status !== 'Success') {
          throw new Error(res.ErrMessage || 'Request Failed')
        }
        if (!Array.isArray(res.Data.Table2) || res.Data.Table2.length === 0) {
          throw new Error('Invalid response data')
        }
        const standardList = res.Data.Table.find((item) => item.CompanyID === 0)
        const customisedList = res.Data.Table.find((item) => item.CompanyID !== 0)
        const activeList = res.Data.Table1[0]
        const list = res.Data.Table2.map((item) => ({
          ...item,
          customised: customisedList[item.SmsTemplateID] || '',
          standard: standardList[item.SmsTemplateID] || '',
          isActive: activeList[item.SmsPolicyID],
        }))
        setTableData(list)
        setTags(res.Data.Table3 || [])
        setIsStandard(activeList.IsStandard)
        setSMSSenderID(activeList.SMSSenderID)
        setSMSEntityID(customisedList.SMSEntityID)
        setIsDLTLock(to01(activeList.IsDLTLock))
        setPolicyData(activeList)
        setTemplateIds({
          standard: standardList?.TemplateID ?? 0,
          customised: customisedList?.TemplateID ?? 0,
        })
      })
      .catch((err) => {
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => setLoader(false))
  }
  useEffect(() => {
    fetchData()
  }, [])

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const nextHeight = Math.max(200, window.innerHeight - rect.top - 10)
      setAvailableHeight(nextHeight)
    }
    updateHeight()
    window.addEventListener('resize', updateHeight)
    return () => window.removeEventListener('resize', updateHeight)
  }, [])

  const toggleActive = (policyId) => {
    setPolicyData((prev) => ({
      ...prev,
      [policyId]: prev?.[policyId] ? 0 : 1,
    }))
    setTableData((prev) => prev.map((item) => (item.SmsPolicyID === policyId ? { ...item, isActive: item.isActive ? 0 : 1 } : item)))
  }
  const updateTemplateBody = (templateId, value, isStandard) => {
    setTableData((prev) =>
      prev.map((item) =>
        item.SmsTemplateID === templateId
          ? {
              ...item,
              standard: isStandard ? value : item.standard,
              customised: isStandard ? item.customised : value,
            }
          : item
      )
    )
  }

  const handleRequestOtp = () => {
    const nextAction = isDLTLock ? 'Lock' : 'Unlock'
    const nextOtpType = isDLTLock ? 'SL' : 'SUL'
    const payload = {
      strOTPType: nextOtpType,
      intCompanyID,
      intUserID,
      strResult: 'SMS Template DLT Lock',
    }
    PostRequestV2(urls.generateOTPForSMSLock, payload)
      .then((res) => {
        if (res?.status !== 'Success') {
          throw new Error(res?.ErrMessage || 'Request Failed')
        }
        setOtpType(nextOtpType)
        setOtpAction(nextAction)
        setOtpValue('')
        setOtpModalOpen(true)
      })
      .catch((err) => {
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
  }

  const handleVerifyOtp = () => {
    if (!otpValue) {
      setAlert({
        show: true,
        msg: 'Please enter OTP',
        nextId: '',
      })
      return
    }
    const payload = {
      strOTPType: otpType,
      intOTP: otpValue,
      intCompanyId: intCompanyID,
    }
    GetRequest(urls.verifyOTPForSMSLock, payload)
      .then((res) => {
        if (res?.status !== 'Success') {
          throw new Error(res?.ErrMessage || 'OTP verification failed')
        }
        setIsDLTLock((prev) => (prev ? 0 : 1))
        setOtpModalOpen(false)
        setOtpValue('')
        setAlert({
          show: true,
          msg: res?.ErrMessage || `${otpAction} successful`,
          nextId: '',
        })
      })
      .catch((err) => {
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
  }

  const templateKeyMap = {
    SenderSMSBooking: 'SenderBookingSMS',
    ReceiverSMSBooking: 'ReceiverBookingSMS',
    SenderSMSDispatch: 'SenderDispatchSMS',
    ReceiverSMSDispatch: 'ReceiverDispatchSMS',
    SenderSMSDelivery: 'SenderDeliverySMS',
    ReceiverSMSDelivery: 'ReceiverDeliverySMS',
    ReceiverSMSReceive: 'ReceiverReceiveSMS',
    BranchAgentEditOTP: 'BranchAgentEdit',
    FOCBookingOTP: 'FOCBookingSMS',
    BranchInquirySMS: 'BranchInquirySMS',
    ReceiverSMSNotification: 'ReceiverNotificationSMS',
    SenderSMSCancellation: 'SenderCancellationSMS',
    ReceiverSMSCancellation: 'ReceiverCancellationSMS',
    CargoCustomerBookingInquirySMS: 'CargoCustomerBookingInquirySMS',
    CargoOperatorBookingInquirySMS: 'CargoOperatorBookingInquirySMSSMS',
    ReceiverSMSDemmurage: 'ReceiverDemmurageSMS',
    AdminReverseLuggageSMS: 'AdminReverseLuggage',
    ReceiverOtpDeliverySMS: 'ReceiverDeliveryOTP',
    LRStatusViewOTP: 'LRTrackingLuggageSMS',
    GeneralEventEditOTPSMS: 'RateKgKmEditSMS',
  }

  const buildTemplatePayload = () => {
    const payload = {
      CompanyID: intCompanyID,
      TemplateID: IsStandard ? templateIds.standard : templateIds.customised,
      SMSEntityID: policyData?.SMSEntityID || SMSEntityID || '',
    }
    tableData.forEach((card) => {
      const key = templateKeyMap[card.SmsTemplateID]
      if (!key) return
      payload[key] = IsStandard ? card.standard : card.customised
    })
    return payload
  }

  const buildPolicyPayload = () => {
    const p = policyData || {}
    return {
      CompanyID: intCompanyID,
      SMSPolicyID: p.SMSPolicyID || 0,
      IsSenderMsgBkg: to01(p.IsSenderBooking),
      IsReceiveMsgBkg: to01(p.IsReceiverBooking),
      IsSenderMsgDispatch: to01(p.IsSenderDispatch),
      IsReceiverMsgDispatch: to01(p.IsReceiverDispatch),
      IsSenderMsgDel: to01(p.IsSenderDelivery),
      IsReceiveMsgDel: to01(p.IsReceiverDelivery),
      IsReceiverReceive: to01(p.IsReceiverReceive),
      intBranchAgentEdit: to01(p.IsBranchAgentEdit),
      intFOCBookingOTP: to01(p.IsFOCBooking),
      IsBranchInquiry: to01(p.IsBranchInquiry),
      IsReceiveMsgNotification: to01(p.IsReceiverNotification),
      IsSenderMsgCancel: to01(p.IsSenderCancellation),
      IsReceiveMsgCancel: to01(p.IsReceiverCancellation),
      IsInqMsgCustomer: to01(p.IsCargoCustomerBookingInquirySMS),
      IsInqMsgOperator: to01(p.IsCargoOperatorBookingInquirySMS),
      IsReceiverDemmurage: to01(p.IsReceiverDemmurage),
      intAdminRevLuggageOTP: to01(p.IsAdminReverseLuggage),
      intCargoRecDelOTP: to01(p.IsReceiverOtpDeliverySMS),
      intLRTrackingOtp: to01(p.IsLRTrackingMS),
      intRateKGKMEditOTP: to01(p.IsRateKgKmEdit),
      IsReceiverDemmurageOnce: to01(p.IsDemSMSOnce),
      IsReceiverDemmuragePeriod: to01(p.IsDemurrageSMSRepeat),
      IsRecMsgNTHr: p.HrsAfterReceive ?? 0,
      strInqMsgOpMobile: p.CargoBookingInquiryOperatorPhoneNos ?? '',
      IsRecMsgDemDays: p.IsDemurrageSMSRepeatDays ?? 0,
      IsCargoOutDeliveryOTP: to01(p.IsCargoOutDeliveryOTP),
    }
  }

  const handleSave = () => {
    const templatePayload = buildTemplatePayload()
    const policyPayload = buildPolicyPayload()
    setLoader(true)
    Promise.all([PostRequest(urls.cargoSMSTemplateUpdate, templatePayload), PostRequest(urls.cargoSMSPoliciesUpdate, policyPayload)])
      .then(([templateRes, policyRes]) => {
        if (templateRes?.status !== 'Success') {
          throw new Error(templateRes?.ErrMessage || 'Template update failed')
        }
        if (policyRes?.status !== 'Success') {
          throw new Error(policyRes?.ErrMessage || 'Policy update failed')
        }
        setAlert({
          show: true,
          msg: 'SMS template and policies updated successfully',
          nextId: '',
          success: true,
        })
      })
      .catch((err) => {
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => setLoader(false))
  }

  const showStandardGuidelines = () => {
    setAlert({
      show: true,
      msg: (
        <Box sx={{ padding: '6px 2px', textAlign: 'left' }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '10px', textAlign: 'left' }}>
            Important GuideLines For Customized SMS Templates
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px', textAlign: 'left' }}>
            <Typography sx={{ textAlign: 'left' }}>
              1. Use predefined SMS tags (mentioned at bottom of the main screen) only while creating SMS templates. Eg: Your PNR:[PNR]
            </Typography>
            <Typography sx={{ textAlign: 'left' }}>2. All customized templates should be whitelisted in the DLT Portal.</Typography>
            <Typography sx={{ textAlign: 'left' }}>
              3. Template id should be append with approved SMS template using double hash ##. Eg: SMS Template ##templateID
            </Typography>
            <Typography sx={{ textAlign: 'left' }}>4. Custom Template will send through operator's sender tag.</Typography>
          </Box>
        </Box>
      ),
      nextId: '',
      success: true,
    })
  }

  const handleTemplateModeChange = (nextIsStandard) => {
    if (!nextIsStandard && IsStandard) {
      showStandardGuidelines()
    }
    setIsStandard(nextIsStandard)
  }
  const handleTestSms = (card) => {
    setSelectedTestSmsCard(card)
    setTestSmsPhoneNumber('')
    setTestSmsModalOpen(true)
  }

  const confirmTestSms = () => {
    const phoneNumber = testSmsPhoneNumber.replace(/\D/g, '')
    if (!phoneNumber) {
      setAlert({
        show: true,
        msg: 'Please enter phone number',
        nextId: '',
      })
      return
    }
    if (!selectedTestSmsCard) return
    const payload = {
      intCompanyId: intCompanyID,
      strOTPType: selectedTestSmsCard.SmsType,
      PhoneNumber: phoneNumber,
      userId: intUserID,
      OTP: 0,
      EntityType: 'Company',
      IsStandard,
      MessageTemplate: IsStandard ? selectedTestSmsCard.standard : selectedTestSmsCard.customised,
    }
    setLoader(true)
    GetRequest(urls.sendSMSOTPForSMSPolicies, payload)
      .then((res) => {
        if (res?.status !== 'Success') {
          throw new Error(res?.ErrMessage || 'Failed to send test SMS')
        }
        setTestSmsModalOpen(false)
        setSelectedTestSmsCard(null)
        setTestSmsPhoneNumber('')
        setAlert({
          show: true,
          msg: res?.ErrMessage || 'Test SMS sent successfully',
          nextId: '',
          success: true,
        })
      })
      .catch((err) => {
        setAlert({
          show: true,
          msg: err.message,
          nextId: '',
        })
      })
      .finally(() => setLoader(false))
  }

  const switchToStandard = (card) => {
    setSwitchConfirmCard(card)
  }

  const confirmSwitchToStandard = () => {
    if (!switchConfirmCard) return
    setTableData((prev) =>
      prev.map((item) =>
        item.SmsTemplateID === switchConfirmCard.SmsTemplateID
          ? {
              ...item,
              customised: item.standard,
            }
          : item
      )
    )
    setSwitchConfirmCard(null)
  }
  return (
    <Box
      ref={containerRef}
      sx={{
        height: availableHeight ? `${availableHeight}px` : 'auto',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          padding: '10px 24px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
          <Radio checked={IsStandard === 1} onChange={() => handleTemplateModeChange(1)} sx={{ padding: '2px' }} />
          <Typography sx={{ fontWeight: 700, color: '#3a3a3a' }}>Standard Template</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
          <Radio checked={!IsStandard} onChange={() => handleTemplateModeChange(0)} sx={{ padding: '2px' }} />
          <Typography sx={{ fontWeight: 700, color: '#3a3a3a' }}>Customised Template</Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #d8d8d8',
            borderRadius: '18px',
            padding: '3px 12px',
            minWidth: '340px',
            background: '#fff',
          }}
        >
          <InfoIcon sx={{ color: theme.palette.primary.main, fontSize: '20px', marginRight: '6px' }} />
          <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.1 }}>SMS will be sent through</Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#37a2e9', marginLeft: '4px', lineHeight: 1.1 }}>
            {SMSSenderID}
          </Typography>
        </Box>

        <div className={'input--wrapper'} style={{ margin: 0 }}>
          <LineinputV2 label={'EntityID'} value={SMSEntityID || ''} disabled={true} />
        </div>
        <Button
          variant="outlined"
          onKeyDown={(e) => {}}
          id={'cityPairSearch_btn'}
          sx={{ height: '36px', paddingY: '6px !important' }}
          className="btn cancel-btn"
          onClick={handleRequestOtp}
        >
          {isDLTLock ?'Unlock'  :  'Lock'}
        </Button>
        <Box>
          <Typography>For CRSID - {intCompanyID}</Typography>
          <Typography>DLT Approved Template</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'end', flexGrow: 1 }}>
          <Button
            variant="contained"
            onKeyDown={(e) => {}}
            id={'search_btn'}
            sx={{ width: '40px', height: '36px', paddingY: '6px !important' }}
            className="btn btn-primary"
            onClick={handleSave}
          >
            Save
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          borderBottom: '1px solid #cfd4dc',
          padding: '0 24px',
        }}
      >
        <IconButton
          onClick={() => scrollCards(-1)}
          sx={{
            position: 'absolute',
            left: '5px',
            top: '42%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            color: '#fff',
            background: theme.palette.secondary.main,
            border: `2px solid ${theme.palette.primary.main}`,
            '&:hover': { background: theme.palette.primary.main },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Box
          ref={scrollRef}
          sx={{
            display: 'grid',
            gridAutoFlow: 'column',
            gridTemplateRows: 'repeat(2, auto)',
            gridAutoColumns: '250px',
            gap: '10px',
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            alignItems: 'stretch',
            '&::-webkit-scrollbar': { height: '8px' },
            '&::-webkit-scrollbar-thumb': { background: '#b8c2d0', borderRadius: '8px' },
          }}
        >
          {tableData.map((card) => {
            const hasExtraInputs =
              (Array.isArray(card.config) && card.config.length) ||
              card.SmsTemplateID === 'ReceiverSMSNotification' ||
              card.SmsTemplateID === 'CargoOperatorBookingInquirySMS' ||
              card.SmsTemplateID === 'ReceiverSMSDemmurage'
            const textRows = card.SmsTemplateID === 'ReceiverSMSDemmurage' ? 5 : hasExtraInputs ? 6 : 8
            return (
            <Box key={card.SmsTemplateID} sx={cardSx}>
              <Box sx={{ ...cardHeaderSx, background: theme.palette.primary.main }}>
                <Checkbox
                  checked={!!card.isActive}
                  onChange={() => toggleActive(card.SmsPolicyID)}
                  icon={<RadioButtonUncheckedRoundedIcon />}
                  checkedIcon={<CheckCircleRoundedIcon />}
                  disableRipple
                  sx={{
                    padding: 0,
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-checked': { color: '#fff' },
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                />
                <Tooltip
                  title={card.SmsTemplateHeader || ''}
                  placement="top"
                  arrow
                  slotProps={{
                    tooltip: { sx: { fontSize: '14px' } },
                  }}
                >
                  <Typography sx={{ ...truncateText, fontWeight: 700, color: '#fff', fontSize: '15px' }}>
                    {card.SmsTemplateHeader}
                  </Typography>
                </Tooltip>
              </Box>
              <Box sx={{ padding: 0 }}>
                <TextField
                  value={IsStandard ? card.standard : card.customised}
                  onChange={(e) => updateTemplateBody(card.SmsTemplateID, e.target.value, !!IsStandard)}
                  multiline
                  rows={textRows}
                  disabled={IsStandard || card.IsEnabled}
                  placeholder={IsStandard ? 'Standard template' : 'Customised template'}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '15px',
                      color: '#32343a',
                      lineHeight: 1.55,
                      background: '#fff',
                      padding: '8px',
                      border: 0,
                    },
                    '& .MuiOutlinedInput-notchedOutline': { border: 0 },
                  }}
                />
                {Array.isArray(card.config) && card.config.length ? (
                  <Box sx={{ padding: '0 10px 8px' }}>
                    {card.config.map((cfg) => (
                      <TextField
                        key={`${card.id}_${cfg.label}`}
                        label={cfg.label}
                        defaultValue={cfg.value}
                        size="small"
                        sx={{ width: '100%', background: '#fff' }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { fontSize: '14px', padding: '6px 8px' } }}
                      />
                    ))}
                  </Box>
                ) : null}

                {card.SmsTemplateID === 'ReceiverSMSNotification' ? (
                  <Box sx={{ padding: '0 10px 8px' }}>
                    <TextField
                      label="Hrs"
                      value={policyData?.HrsAfterReceive ?? ''}
                      size="small"
                      sx={{ width: '100%', background: '#fff' }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { fontSize: '14px', padding: '6px 8px' } }}
                      onChange={(e) => setPolicyData((prev) => ({ ...prev, HrsAfterReceive: e.target.value }))}
                    />
                  </Box>
                ) : null}

                {card.SmsTemplateID === 'CargoOperatorBookingInquirySMS' ? (
                  <Box sx={{ padding: '0 10px 8px' }}>
                    <TextField
                      label="Mobile Nos"
                      value={policyData?.CargoBookingInquiryOperatorPhoneNos ?? ''}
                      size="small"
                      sx={{ width: '100%', background: '#fff' }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { fontSize: '14px', padding: '6px 8px' } }}
                      onChange={(e) => setPolicyData((prev) => ({ ...prev, CargoBookingInquiryOperatorPhoneNos: e.target.value }))}
                    />
                  </Box>
                ) : null}

                {card.SmsTemplateID === 'ReceiverSMSDemmurage' ? (
                  <Box sx={{ padding: '0 10px 8px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '6px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Radio
                          checked={!!policyData?.IsDemSMSOnce}
                          sx={{ padding: '2px' }}
                          onChange={() =>
                            setPolicyData((prev) => ({
                              ...prev,
                              IsDemSMSOnce: 1,
                              IsDemurrageSMSRepeat: 0,
                            }))
                          }
                        />
                        <Typography sx={{ fontSize: '13px' }}>Once?</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Radio
                          checked={!!policyData?.IsDemurrageSMSRepeat}
                          sx={{ padding: '2px' }}
                          onChange={() =>
                            setPolicyData((prev) => ({
                              ...prev,
                              IsDemSMSOnce: 0,
                              IsDemurrageSMSRepeat: 1,
                            }))
                          }
                        />
                        <Typography sx={{ fontSize: '13px' }}>Periodically?</Typography>
                      </Box>
                    </Box>
                    <TextField
                      label="Days"
                      value={policyData?.IsDemurrageSMSRepeatDays ?? ''}
                      size="small"
                      sx={{ width: '100%', background: '#fff' }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { fontSize: '14px', padding: '6px 8px' } }}
                      onChange={(e) => setPolicyData((prev) => ({ ...prev, IsDemurrageSMSRepeatDays: e.target.value }))}
                    />
                  </Box>
                ) : null}
              </Box>
              <Box
                sx={{
                  marginTop: 'auto',
                  borderTop: '1px solid #d6dbe3',
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '7px 10px',
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ fontSize: '12px', fontWeight: 500, minWidth: 'auto', padding: '0 4px' }}
                  onClick={() => handleTestSms(card)}
                >
                  Test SMS
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={IsStandard}
                  sx={{ fontSize: '12px', fontWeight: 500, minWidth: 'auto', padding: '0 4px' }}
                  onClick={() => switchToStandard(card)}
                >
                  Switch to Standard
                </Button>
              </Box>
            </Box>
          )
          })}
        </Box>

        <IconButton
          onClick={() => scrollCards(1)}
          sx={{
            position: 'absolute',
            right: '5px',
            top: '42%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            color: '#fff',
            background: theme.palette.secondary.main,
            border: `2px solid ${theme.palette.primary.main}`,
            '&:hover': { background: theme.palette.primary.main },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      <Box
        sx={{ background: '#f4f4f5', border: '1px solid #cfd4dc', borderTop: 0, padding: '10px 14px 14px', display: 'flex', gap: '10px' }}
      >
        <Box>
          <Typography sx={{ fontSize: '24px', fontWeight: 800, letterSpacing: '0.3px' }}>TAGS:</Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            width: '100%',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(140px, 1fr))',
              md: 'repeat(4, minmax(140px, 1fr))',
              xl: 'repeat(6, minmax(150px, 1fr))',
            },
            gap: '0px 10px',
          }}
        >
          {tags.map(({ Tag, TagDescription }, index) => (
            <Box key={`${index}_${Tag}`} sx={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
              <Box
                sx={{
                  minWidth: '52px',
                  border: '1px solid #cfd3db',
                  borderRadius: '4px',
                  textAlign: 'center',
                  fontSize: '12px',
                  color: '#2d3d53',
                  fontWeight: 700,
                  background: '#f7f7f8',
                  padding: '2px 5px',
                }}
              >
                {Tag}
              </Box>
              <Typography sx={{ fontSize: '13px', color: '#3c4046' }}>{TagDescription}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      {otpModalOpen ? (
        <SingleInputModal
          data={{
            headTxt: `${otpAction} DLT Template`,
            inputId: 'sms_dlt_otp',
            inputLabel: 'Enter OTP',
            inputName: 'sms_dlt_otp',
            inputType: 'number',
            inputValue: otpValue,
            mainButtonLabel: 'Confirm OTP',
            mainButtonId: 'sms_dlt_otp_confirm',
          }}
          functions={{
            updateModal: () => setOtpModalOpen(false),
            onChangeInput: (val) => setOtpValue(val),
            onClickButton: handleVerifyOtp,
            onCloseCallback: () => setOtpModalOpen(false),
          }}
        />
      ) : null}
      {testSmsModalOpen ? (
        <SingleInputModal
          data={{
            headTxt: 'Test SMS',
            inputId: 'test_sms_phone_number',
            inputLabel: 'Phone Number',
            inputName: 'test_sms_phone_number',
            inputType: 'text',
            inputValue: testSmsPhoneNumber,
            mainButtonLabel: 'Yes',
            mainButtonId: 'test_sms_confirm',
          }}
          functions={{
            updateModal: () => {
              setTestSmsModalOpen(false)
              setSelectedTestSmsCard(null)
            },
            onChangeInput: (val) => setTestSmsPhoneNumber(val.replace(/\D/g, '')),
            onClickButton: confirmTestSms,
            onCloseCallback: () => {
              setTestSmsModalOpen(false)
              setSelectedTestSmsCard(null)
            },
          }}
        />
      ) : null}
      {switchConfirmCard ? (
        <SuccessPopUp
          data={{
            headTxt: 'Switch to Standard',
            successMessage: 'Do you want to replace customised template with standard template for this card?',
            buttonId1: 'switch_to_standard_yes',
            buttonId2: 'switch_to_standard_no',
            yesAction: confirmSwitchToStandard,
            noAction: () => setSwitchConfirmCard(null),
            yesDefault: true,
          }}
          updateModal={setSwitchConfirmCard}
        />
      ) : null}
    </Box>
  )
}
